import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useImageContext } from "../../../contexts/ImageContext";
import { useImagePickerModal } from "../../../hooks/app/useImagePickerModal";
import {
  libraryItemCreationId,
  libraryItemExportId,
} from "../../../utils/openLibraryInEditor";
import {
  fetchCreationImageBlob,
  fetchExportImageBlob,
  fetchUploadImageBlob,
} from "../../../services/api/editor/background_removal";
import { registerLibraryUploadOnce } from "../../../utils/registerLibraryUploadOnce";
import { runWithMinUploadProgress } from "../../../utils/runWithMinUploadProgress";
import { formatFileSize } from "../../../utils/formatFileSize";
import { getUploadSizeError } from "../../../utils/uploadLimits";

/**
 * Shared image source for tool-prep popups (upload inside Original card).
 * Returns picker props + source URL + clear/upload handlers.
 */
export function useToolPrepImageSource({
  originalImageUrl = null,
  imageFile = null,
  busy = false,
  onSourceChange = null,
} = {}) {
  const { getToken } = useAuth();
  const {
    inputPreview,
    selectedFile: contextFile,
    setFile,
    clearImage,
  } = useImageContext();
  const { openImagePicker, imagePickerProps } = useImagePickerModal();

  const [pickedFile, setPickedFile] = useState(null);
  const [libraryPreviewUrl, setLibraryPreviewUrl] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [sourceCleared, setSourceCleared] = useState(false);
  const [sourceError, setSourceError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [librarySourceMeta, setLibrarySourceMeta] = useState(null);

  const resolvedFile = pickedFile || imageFile || contextFile || null;

  useEffect(() => {
    if (!resolvedFile) {
      setFilePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(resolvedFile);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [resolvedFile]);

  useEffect(() => {
    return () => {
      if (libraryPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(libraryPreviewUrl);
      }
    };
  }, [libraryPreviewUrl]);

  const clearLibraryPreview = useCallback(() => {
    setLibraryPreviewUrl((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const sourceImageUrl = sourceCleared
    ? null
    : filePreviewUrl ||
      libraryPreviewUrl ||
      originalImageUrl ||
      inputPreview ||
      null;

  const clearSource = useCallback(() => {
    if (busy || uploadProgress != null) return;
    setSourceCleared(true);
    setPickedFile(null);
    clearLibraryPreview();
    setLibrarySourceMeta(null);
    setSourceError(null);
    setUploadProgress(null);
    try {
      clearImage?.();
    } catch {
      // ignore
    }
    onSourceChange?.({ file: null, imageUrl: null });
  }, [busy, clearImage, clearLibraryPreview, onSourceChange, uploadProgress]);

  const applyPickedFile = useCallback(
    async (file) => {
      if (!file) return;
      const sizeError = getUploadSizeError(file);
      if (sizeError) {
        setSourceError(sizeError);
        return;
      }
      setSourceCleared(false);
      clearLibraryPreview();
      setLibrarySourceMeta(null);
      setPickedFile(file);
      setSourceError(null);
      try {
        setFile(file);
      } catch {
        // ignore
      }
      onSourceChange?.({ file, imageUrl: null });

      try {
        await runWithMinUploadProgress({
          setProgress: setUploadProgress,
          work: async () => {
            try {
              const token = await getToken();
              if (token) await registerLibraryUploadOnce(token, file);
            } catch {
              // Non-blocking
            }
          },
        });
      } catch (err) {
        setSourceError(err?.message || "Could not load image.");
        setUploadProgress(null);
      }
    },
    [clearLibraryPreview, getToken, onSourceChange, setFile]
  );

  const applyLibraryItem = useCallback(
    async (item) => {
      if (!item) return;
      setSourceError(null);
      try {
        let blob = null;
        let creationId = null;
        let exportId = null;

        await runWithMinUploadProgress({
          setProgress: setUploadProgress,
          minMs: 1400,
          work: async () => {
            const token = await getToken();
            if (!token) throw new Error("Sign in required.");

            exportId = libraryItemExportId(item);
            creationId = libraryItemCreationId(item);

            if (item.kind === "upload") {
              const uploadId =
                item.root_creation_id ?? item.creation_id ?? creationId;
              blob = await fetchUploadImageBlob(token, uploadId);
            } else if (item.kind === "export" || exportId) {
              blob = await fetchExportImageBlob(token, exportId);
            } else if (creationId) {
              blob = await fetchCreationImageBlob(token, creationId);
            } else if (item.output_url) {
              const res = await fetch(item.output_url, { mode: "cors" });
              if (!res.ok) throw new Error(`Failed to load image (${res.status})`);
              blob = await res.blob();
            } else {
              throw new Error("Could not resolve library image.");
            }

            if (!blob || blob.type?.includes("json")) {
              throw new Error("Could not load library image.");
            }

            const objectUrl = URL.createObjectURL(blob);
            setSourceCleared(false);
            setPickedFile(null);
            setLibrarySourceMeta({
              name:
                item.file_name ||
                item.name ||
                item.title ||
                "Library image",
              sizeLabel:
                item.size != null && Number.isFinite(Number(item.size))
                  ? formatFileSize(item.size)
                  : item.file_size != null &&
                      Number.isFinite(Number(item.file_size))
                    ? formatFileSize(item.file_size)
                    : blob?.size
                      ? formatFileSize(blob.size)
                      : null,
            });
            setLibraryPreviewUrl((prev) => {
              if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
              return objectUrl;
            });
            onSourceChange?.({
              file: null,
              imageUrl: objectUrl,
              creationId,
              exportId,
            });
          },
        });
      } catch (err) {
        const raw = err?.message || "";
        const friendly =
          /failed to fetch|network error|err_connection/i.test(raw)
            ? "Could not load that image. Check that the API is running, then try again."
            : raw || "Could not load selected image.";
        setSourceError(friendly);
        setUploadProgress(null);
      }
    },
    [getToken, onSourceChange]
  );

  const openUploadPicker = useCallback(() => {
    if (uploadProgress != null) return;
    openImagePicker({
      onPickFile: applyPickedFile,
      onPickLibrary: applyLibraryItem,
    });
  }, [applyLibraryItem, applyPickedFile, openImagePicker, uploadProgress]);

  return {
    sourceImageUrl,
    resolvedFile,
    sourceFileName:
      resolvedFile?.name ||
      (uploadProgress != null ? null : librarySourceMeta?.name) ||
      null,
    sourceFileSizeLabel: resolvedFile
      ? formatFileSize(resolvedFile.size)
      : uploadProgress != null
        ? null
        : librarySourceMeta?.sizeLabel || null,
    sourceError,
    setSourceError,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
    /** Call when a new local/library pick succeeds and tool-local cutout should reset. */
    onAfterSourcePicked: () => setSourceCleared(false),
  };
}
